import { useState, useEffect } from 'react';
import { useNotifications } from '../../../contexts/notification';
import styled from 'styled-components';
import { 
  InteractiveCard,
  EmptyStateCard
} from '../../../styles/components/common/cards';
import { 
  Button, 
} from '../../../styles/components/common/buttons';
import { glassDeep, glassMedium } from '../../../styles/components/common/glassmorphism';

import InternshipReport from '../InternshipReport/InternshipReport';
import MajorCoursesSelection from '../MajorCoursesSelection/MajorCoursesSelection';
import AppealForm from './components/AppealForm';
import CompanyEvaluation from './components/CompanyEvaluation';
import {PageHeader, PageTitle} from '../../../styles/components';

// Mock data for completed internships
const mockCompletedInternships = [
  {
    id: "INT001",
    position: "Software Engineering Intern",
    company: {
      name: "TechCorp Solutions",
      logo: "https://via.placeholder.com/50"
    },
    startDate: "2023-06-01",
    endDate: "2023-08-31",
    duration: 3,
    location: "San Francisco, CA",
    description: "Worked on developing and maintaining web applications using React and Node.js. Collaborated with cross-functional teams to implement new features and fix bugs.",
    report: {
      id: "R001",
      title: "Summer 2023 Software Engineering Internship Report",
      submissionDate: "2023-09-15",
      status: "approved",
      summary: "During my internship at TechCorp Solutions, I gained hands-on experience in full-stack development...",
      learningOutcomes: "Developed proficiency in React, Node.js, and modern web development practices...",
      challenges: "Adapting to the company's codebase and development workflow...",
      solutions: "Implemented a systematic approach to learning the codebase...",
      skillsGained: ["React", "Node.js", "TypeScript", "Agile Development", "Team Collaboration"],
      futureApplications: "The skills gained will be valuable for my future career in software development...",
      advisorFeedback: "Excellent report demonstrating clear learning outcomes and professional growth.",
      isFinalized: true
    },
    helpfulCourses: [
      { id: "CS101", name: "Introduction to Programming", code: "CS101" },
      { id: "CS201", name: "Data Structures and Algorithms", code: "CS201" },
      { id: "CS301", name: "Web Development", code: "CS301" }
    ],
    companyEvaluation: {
      workEnvironment: 4,
      mentorship: 5,
      learningOpportunities: 5,
      workLifeBalance: 3,
      compensation: 4,
      overallExperience: 4,
      strengths: "Great mentorship program with senior engineers. Regular feedback sessions. Exposure to latest technologies and modern development practices.",
      improvements: "Work-life balance could be improved during crunch periods. More structured onboarding process would be beneficial for new interns.",
      additionalComments: "Overall, a fantastic experience that significantly improved my technical skills and professional network.",
      recommended: true,
      submissionDate: "2023-09-20"
    }
  },
  {
    id: "INT002",
    position: "Data Science Intern",
    company: {
      name: "DataAnalytics Inc",
      logo: "https://via.placeholder.com/50"
    },
    startDate: "2023-01-01",
    endDate: "2023-03-31",
    duration: 3,
    location: "New York, NY",
    description: "Assisted in developing machine learning models for customer behavior analysis. Worked with large datasets and implemented data preprocessing pipelines.",
    report: {
      id: "R002",
      title: "Winter 2023 Data Science Internship Report",
      submissionDate: "2023-04-15",
      status: "pending",
      summary: "My internship at DataAnalytics Inc provided valuable experience in data science and machine learning...",
      learningOutcomes: "Gained expertise in Python, pandas, and scikit-learn...",
      challenges: "Working with large, unstructured datasets...",
      solutions: "Developed efficient data preprocessing pipelines...",
      skillsGained: ["Python", "Machine Learning", "Data Analysis", "SQL", "Data Visualization"],
      futureApplications: "The experience will be beneficial for my future work in data science...",
      advisorFeedback: "Good report showing technical growth and practical application of skills.",
      isFinalized: true
    },
    helpfulCourses: [
      { id: "DS101", name: "Introduction to Data Science", code: "DS101" },
      { id: "DS201", name: "Machine Learning", code: "DS201" },
      { id: "DS301", name: "Big Data Analytics", code: "DS301" }
    ]
  },
  {
    id: "INT003",
    position: "UX Design Intern",
    company: {
      name: "CreativeUI",
      logo: "https://via.placeholder.com/50"
    },
    startDate: "2022-09-01",
    endDate: "2022-12-31",
    duration: 4,
    location: "Austin, TX",
    description: "Collaborated with design team to create user interfaces and experiences for mobile and web applications. Conducted user research and usability testing.",
    report: {
      id: "R003",
      title: "Fall 2022 UX Design Internship Report",
      submissionDate: "2023-01-10",
      status: "flagged",
      summary: "During my internship at CreativeUI, I gained valuable experience in user experience design and research...",
      learningOutcomes: "Learned user-centered design processes and improved my skills with design tools...",
      challenges: "Balancing user needs with business requirements...",
      solutions: "Developed a framework for evaluating design decisions...",
      skillsGained: ["Figma", "User Research", "Prototyping", "Usability Testing", "Design Thinking"],
      futureApplications: "The design principles I learned will inform my approach to product development...",
      advisorFeedback: "",
      rejectionReason: "The report lacks specific examples of your contributions to projects. Please add at least 2-3 detailed case studies of your work, including your design process and outcomes.",
      isFinalized: true
    },
    helpfulCourses: [
      { id: "DES101", name: "Introduction to Design", code: "DES101" },
      { id: "DES201", name: "User Experience Design", code: "DES201" }
    ]
  },
  {
    id: "INT004",
    position: "Marketing Intern",
    company: {
      name: "Global Brand Inc",
      logo: "https://via.placeholder.com/50"
    },
    startDate: "2022-06-01",
    endDate: "2022-08-31",
    duration: 3,
    location: "Chicago, IL",
    description: "Assisted in developing and executing marketing campaigns. Conducted market research and analyzed campaign performance data.",
    report: {
      id: "R004",
      title: "Summer 2022 Marketing Internship Report",
      submissionDate: "2022-09-05",
      status: "rejected",
      summary: "My internship at Global Brand Inc provided valuable insights into marketing strategy and execution...",
      learningOutcomes: "Gained practical experience in digital marketing and analytics...",
      challenges: "Managing multiple campaign deadlines...",
      solutions: "Implemented project management tools and workflows...",
      skillsGained: ["Social Media Marketing", "Content Creation", "Analytics", "Campaign Management", "SEO"],
      futureApplications: "The marketing skills I gained will be applicable to various industries and roles...",
      advisorFeedback: "",
      rejectionReason: "This report does not meet the minimum academic standards required. There is insufficient critical analysis of your experience, and the report fails to connect your internship to relevant marketing theories discussed in your coursework. Additionally, the report contains several grammatical errors and lacks professional formatting.",
      isFinalized: true
    },
    helpfulCourses: [
      { id: "MKT101", name: "Principles of Marketing", code: "MKT101" },
      { id: "MKT201", name: "Digital Marketing", code: "MKT201" }
    ],
    companyEvaluation: {
      workEnvironment: 2,
      mentorship: 1,
      learningOpportunities: 2,
      workLifeBalance: 3,
      compensation: 2,
      overallExperience: 2,
      strengths: "Good exposure to real-world marketing campaigns. Opportunity to work with diverse marketing channels including social media, email, and content marketing.",
      improvements: "Very limited mentorship and guidance. Interns were often left to figure things out on their own. More structured learning opportunities and regular feedback sessions would greatly improve the internship experience.",
      additionalComments: "The internship felt more like cheap labor than an educational opportunity. Most tasks were repetitive and didn't align with learning objectives.",
      recommended: false,
      submissionDate: "2022-09-10"
    }
  },
  {
    id: "INT005",
    position: "Business Analyst Intern",
    company: {
      name: "ConsultCorp",
      logo: "https://via.placeholder.com/50"
    },
    startDate: "2023-09-01",
    endDate: "2023-12-15",
    duration: 3.5,
    location: "Boston, MA",
    description: "Worked on data analysis projects to provide business insights. Created reports and dashboards to help business stakeholders make informed decisions.",
    helpfulCourses: [
      { id: "BA101", name: "Business Analytics", code: "BA101" },
      { id: "BA201", name: "Data Visualization", code: "BA201" }
    ]
  }
];

// Styled components for the report management page
const ReportManagementContainer = styled.div`
  padding: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ContentContainer = styled.div`
  ${glassDeep}
  border-radius: 12px;
  padding: 1.5rem;
  min-height: 500px;
  transition: all 0.3s ease;
`;

const InternshipsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InternshipCard = styled(InteractiveCard)`
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.3);
  }
  
  .internship-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 1rem;
    
    h3 {
      margin: 0;
      color: rgba(255, 255, 255, 0.9);
      font-size: 1.25rem;
    }
    
    .status-badge {
      padding: 0.25rem 0.75rem;
      border-radius: 9999px;
      font-size: 0.75rem;
      font-weight: 600;
      background: ${props => props.statusColor || 'rgba(99, 102, 241, 0.3)'};
      color: white;
    }
  }
  
  .internship-details {
    flex: 1;
    
    p {
      margin: 0.5rem 0;
      color: rgba(255, 255, 255, 0.8);
      font-size: 0.875rem;
      
      strong {
        color: rgba(255, 255, 255, 0.95);
        margin-right: 0.5rem;
      }
    }
  }
  
  .internship-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const SelectedInternshipDetails = styled.div`
  ${glassMedium}
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  .details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    
    h2 {
      margin: 0;
      color: rgba(255, 255, 255, 0.9);
      font-size: 1.5rem;
    }
  }
  
  .details-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    
    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
    
    .company-info, .report-info {
      h3 {
        color: rgba(255, 255, 255, 0.9);
        margin-top: 0;
        margin-bottom: 1rem;
        font-size: 1.25rem;
        position: relative;
        
        &:after {
          content: '';
          position: absolute;
          bottom: -0.5rem;
          left: 0;
          width: 3rem;
          height: 2px;
          background: linear-gradient(90deg, rgba(99, 102, 241, 0.7), transparent);
          border-radius: 1px;
        }
      }
      
      p {
        margin: 0.5rem 0;
        color: rgba(255, 255, 255, 0.8);
        
        strong {
          color: rgba(255, 255, 255, 0.95);
          margin-right: 0.5rem;
        }
      }
    }
  }
  
  .action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  
  &:after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid rgba(255, 255, 255, 0.1);
    border-radius: 50%;
    border-top-color: rgba(99, 102, 241, 0.7);
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(239, 68, 68, 0.1);
  border-left: 4px solid rgba(239, 68, 68, 0.7);
  color: rgba(255, 255, 255, 0.9);
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  svg {
    flex-shrink: 0;
  }
`;

// New styled components for the action selection modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  padding: 20px;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  ${glassDeep}
  border-radius: 18px;
  padding: 2rem;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  position: relative;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  /* Custom scrollbar for better UX */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.2);
    border-radius: 5px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.6);
    border-radius: 5px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.8);
  }
  
  @keyframes slideUp {
    from { transform: translateY(30px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ActionSelectionModal = styled.div`
  ${glassDeep}
  border-radius: 18px;
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
  position: relative;
  animation: slideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  /* Custom scrollbar for better UX */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(15, 23, 42, 0.2);
    border-radius: 5px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(99, 102, 241, 0.6);
    border-radius: 5px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(99, 102, 241, 0.8);
  }
  
  h2 {
    color: rgba(255, 255, 255, 0.9);
    margin-top: 0;
    margin-bottom: 1.5rem;
    font-size: 1.5rem;
    text-align: center;
  }
  
  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  
  .action-button {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.2rem;
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 12px;
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(99, 102, 241, 0.3);
      transform: translateY(-3px);
      box-shadow: 0 7px 14px rgba(0, 0, 0, 0.1);
    }
    
    &:active {
      transform: translateY(-1px);
    }
    
    .icon {
      font-size: 1.5rem;
      color: rgba(99, 102, 241, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 40px;
      
      svg {
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1));
      }
    }
    
    .text {
      flex: 1;
      
      h3 {
        margin: 0 0 0.25rem 0;
        font-size: 1.1rem;
        color: rgba(255, 255, 255, 0.95);
        font-weight: 600;
      }
      
      p {
        margin: 0;
        font-size: 0.875rem;
        color: rgba(255, 255, 255, 0.7);
        line-height: 1.4;
      }
    }
  }
  
  .close-button {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(99, 102, 241, 0.2);
    border: 1px solid rgba(99, 102, 241, 0.3);
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.25rem;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: rgba(99, 102, 241, 0.4);
      color: rgba(255, 255, 255, 1);
      transform: rotate(90deg);
    }
  }
`;

const ReportManagement = () => {
  const { addNotification } = useNotifications();
  const [completedInternships, setCompletedInternships] = useState([]);
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [showCoursesForm, setShowCoursesForm] = useState(false);
  const [showAppealForm, setShowAppealForm] = useState(false);
  const [showActionSelection, setShowActionSelection] = useState(false);
  const [showCompanyEvaluation, setShowCompanyEvaluation] = useState(false);

  useEffect(() => {
    // Simulate API call to fetch completed internships
    const fetchCompletedInternships = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // In a real application, this would be an API call
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setCompletedInternships(mockCompletedInternships);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching completed internships:', error);
        setError('Failed to load your internship data. Please try again later.');
        setLoading(false);
      }
    };

    fetchCompletedInternships();
  }, []);

  const handleSelectInternship = (internship) => {
    setSelectedInternship(internship);
    setShowActionSelection(true);
  };

  const handleShowReportForm = () => {
    if (selectedInternship) {
      // Validate that we have all required properties before showing the form
      if (!selectedInternship.company || !selectedInternship.company.name) {
        console.error("Selected internship missing company data:", selectedInternship);
        addNotification({
          title: "Error",
          message: "Internship data is incomplete. Please try selecting another internship.",
          type: "error",
        });
        return;
      }
      
      setShowReportForm(true);
      setShowActionSelection(false);
      
      // Log to verify the internship data is correctly passed
      console.log("Opening report form with internship:", selectedInternship);
      console.log("Existing report data:", selectedInternship.report);
    } else {
      addNotification({
        title: "Error",
        message: "Please select an internship first.",
        type: "error",
      });
    }
  };

  const handleShowCoursesForm = () => {
    if (selectedInternship) {
      setShowCoursesForm(true);
      setShowActionSelection(false);
    } else {
      addNotification({
        title: "Error",
        message: "Please select an internship first.",
        type: "error",
      });
    }
  };

  const handleShowAppealForm = () => {
    if (selectedInternship && selectedInternship.report && 
        (selectedInternship.report.status === 'flagged' || selectedInternship.report.status === 'rejected')) {
      setShowAppealForm(true);
      setShowActionSelection(false);
    } else {
      addNotification({
        title: "Error",
        message: "You can only appeal flagged or rejected reports.",
        type: "error",
      });
    }
  };

  const handleShowCompanyEvaluation = () => {
    if (selectedInternship) {
      setShowCompanyEvaluation(true);
      setShowActionSelection(false);
    } else {
      addNotification({
        title: "Error",
        message: "Please select an internship first.",
        type: "error",
      });
    }
  };

  const handleSaveReport = (report) => {
    try {
      if (!report) {
        console.error("Report data is undefined or null");
        addNotification({
          title: "Error",
          message: "No report data provided. Please try again.",
          type: "error",
        });
        return;
      }
      
      console.log("Saving report data:", report);
      
      const updatedReport = {
        ...report,
        id: report.id || `R${Date.now()}`,
        submissionDate: report.isFinalized ? new Date().toISOString() : null,
        status: report.isFinalized ? "pending" : "draft"
      };
      
      setCompletedInternships(internships => 
        internships.map(internship => 
          internship.id === selectedInternship.id
            ? { ...internship, report: updatedReport }
            : internship
        )
      );
      
      setSelectedInternship(prev => ({ ...prev, report: updatedReport }));
      
      if (report.isFinalized) {
        addNotification({
          title: "Success",
          message: "Your internship report has been submitted for review.",
          type: "success",
        });
      } else {
        addNotification({
          title: "Draft Saved",
          message: "Your report draft has been saved. You can continue editing later.",
          type: "info",
        });
        
        setShowReportForm(false);
      }
    } catch (error) {
      console.error('Error saving report:', error);
      addNotification({
        title: "Error",
        message: "Failed to save your report. Please try again.",
        type: "error",
      });
    }
  };

  const handleSaveCourses = (courses) => {
    try {
      setCompletedInternships(internships => 
        internships.map(internship => 
          internship.id === selectedInternship.id
            ? { ...internship, helpfulCourses: courses }
            : internship
        )
      );
      
      setSelectedInternship(prev => ({ ...prev, helpfulCourses: courses }));
      setShowCoursesForm(false);
      
      addNotification({
        title: "Success",
        message: "Your course selections have been saved successfully.",
        type: "success",
      });
    } catch (error) {
      console.error('Error saving courses:', error);
      addNotification({
        title: "Error",
        message: "Failed to save your course selections. Please try again.",
        type: "error",
      });
    }
  };

  const handleSubmitAppeal = (appealMessage) => {
    if (!appealMessage.trim()) {
      addNotification({
        title: "Error",
        message: "Please provide an appeal message.",
        type: "error",
      });
      return;
    }

    try {
      const updatedReport = {
        ...selectedInternship.report,
        appealMessage,
        appealDate: new Date().toISOString(),
        status: 'under_appeal'
      };

      setCompletedInternships(internships => 
        internships.map(internship => 
          internship.id === selectedInternship.id
            ? { ...internship, report: updatedReport }
            : internship
        )
      );
      
      setSelectedInternship(prev => ({ 
        ...prev, 
        report: updatedReport
      }));
      
      setShowAppealForm(false);
      
      addNotification({
        title: "Appeal Submitted",
        message: "Your appeal has been submitted successfully and is under review.",
        type: "success",
      });
    } catch (error) {
      console.error('Error submitting appeal:', error);
      addNotification({
        title: "Error",
        message: "Failed to submit your appeal. Please try again.",
        type: "error",
      });
    }
  };

  const handleSaveCompanyEvaluation = (evaluation) => {
    try {
      setCompletedInternships(internships => 
        internships.map(internship => 
          internship.id === selectedInternship.id
            ? { ...internship, companyEvaluation: evaluation }
            : internship
        )
      );
      
      setSelectedInternship(prev => ({ ...prev, companyEvaluation: evaluation }));
      setShowCompanyEvaluation(false);
      
      if (evaluation === null) {
        addNotification({
          title: "Evaluation Deleted",
          message: "Your company evaluation has been deleted successfully.",
          type: "success",
        });
      } else {
        addNotification({
          title: "Success",
          message: "Your company evaluation has been saved successfully.",
          type: "success",
        });
      }
    } catch (error) {
      console.error('Error saving company evaluation:', error);
      addNotification({
        title: "Error",
        message: "Failed to save your company evaluation. Please try again.",
        type: "error",
      });
    }
  };

  const handleDeleteReport = () => {
    if (window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) {
      try {
        setCompletedInternships(internships => 
          internships.map(internship => 
            internship.id === selectedInternship.id
              ? { ...internship, report: null }
              : internship
          )
        );
        
        setSelectedInternship(prev => ({ ...prev, report: null }));
        
        addNotification({
          title: "Report Deleted",
          message: "Your report has been deleted successfully.",
          type: "success",
        });
      } catch (error) {
        console.error('Error deleting report:', error);
        addNotification({
          title: "Error",
          message: "Failed to delete your report. Please try again.",
          type: "error",
        });
      }
    }
  };

  // handleDeleteCourses function removed - we want users to be able to select courses but not delete them

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending Review';
      case 'approved': return 'Approved';
      case 'flagged': return 'Needs Revision';
      case 'rejected': return 'Rejected';
      case 'under_appeal': return 'Under Appeal';
      default: return 'Draft';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'rgba(245, 158, 11, 0.3)'; // Amber
      case 'approved': return 'rgba(16, 185, 129, 0.3)'; // Green
      case 'flagged': return 'rgba(245, 158, 11, 0.3)'; // Amber
      case 'rejected': return 'rgba(239, 68, 68, 0.3)'; // Red
      case 'under_appeal': return 'rgba(99, 102, 241, 0.3)'; // Indigo
      default: return 'rgba(99, 102, 241, 0.3)'; // Indigo
    }
  };

  const renderReportsTab = () => {
    if (loading) {
      return <LoadingSpinner />;
    }

    if (error) {
      return (
        <ErrorMessage>
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <div>
            <h4 style={{ margin: '0 0 0.5rem 0' }}>Error Loading Data</h4>
            <p style={{ margin: 0 }}>{error}</p>
            <Button 
              onClick={() => window.location.reload()} 
              style={{ marginTop: '1rem' }}
            >
              Try Again
            </Button>
          </div>
        </ErrorMessage>
      );
    }

    if (completedInternships.length === 0) {
      return (
        <EmptyStateCard>
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="currentColor" viewBox="0 0 16 16">
            <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H2z"/>
            <path d="M6.854 4.646a.5.5 0 0 1 0 .708L4.207 8l2.647 2.646a.5.5 0 0 1-.708.708l-3-3a.5.5 0 0 1 0-.708l3-3a.5.5 0 0 1 .708 0zm2.292 0a.5.5 0 0 0 0 .708L11.793 8l-2.647 2.646a.5.5 0 0 0 .708.708l3-3a.5.5 0 0 0 0-.708l-3-3a.5.5 0 0 0-.708 0z"/>
          </svg>
          <h3>No Completed Internships</h3>
          <p>You don't have any completed internships yet. Once you complete an internship, you'll be able to submit reports here.</p>
        </EmptyStateCard>
      );
    }

    return (
      <InternshipsGrid>
        {completedInternships.map(internship => (
          <InternshipCard 
            key={internship.id} 
            onClick={() => handleSelectInternship(internship)}
            statusColor={internship.report ? getStatusColor(internship.report.status) : 'rgba(99, 102, 241, 0.3)'}
          >
            <div className="internship-header">
              <h3>{internship.position}</h3>
              {internship.report && (
                <div className="status-badge">
                  {getStatusLabel(internship.report.status)}
                </div>
              )}
            </div>
            
            <div className="internship-details">
              <p><strong>Company:</strong> {internship.company.name}</p>
              <p><strong>Duration:</strong> {internship.duration} months</p>
              <p><strong>Dates:</strong> {internship.startDate} to {internship.endDate}</p>
              <p><strong>Report:</strong> {internship.report ? (internship.report.isFinalized ? 'Finalized' : 'Draft') : 'Not submitted'}</p>
              <p><strong>Courses:</strong> {internship.helpfulCourses ? 'Selected' : 'Not selected'}</p>
              <p><strong>Evaluation:</strong> {internship.companyEvaluation ? 'Submitted' : 'Not submitted'}</p>
            </div>
            
            <div className="internship-actions">
              <Button onClick={(e) => {
                e.stopPropagation();
                handleSelectInternship(internship);
              }}>
                Manage
              </Button>
            </div>
          </InternshipCard>
        ))}
      </InternshipsGrid>
    );
  };

  // Render the action selection modal
  const renderActionSelectionModal = () => {
    if (!selectedInternship) return null;
    
    return (
      
      <ModalOverlay onClick={() => setShowActionSelection(false)}>
        <ActionSelectionModal onClick={(e) => e.stopPropagation()}>
          <button className="close-button" onClick={() => setShowActionSelection(false)}>&times;</button>
          <h2>Manage {selectedInternship.position}</h2>
          <div className="action-buttons">
            <div className="action-button" onClick={handleShowReportForm}>
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                  <line x1="16" y1="13" x2="8" y2="13"></line>
                  <line x1="16" y1="17" x2="8" y2="17"></line>
                  <polyline points="10 9 9 9 8 9"></polyline>
                </svg>
              </div>
              <div className="text">
                <h3>{selectedInternship.report ? 'View Report' : 'Create Report'}</h3>
                <p>{selectedInternship.report 
                  ? 'View or edit your existing internship report' 
                  : 'Create a new report for this internship'}</p>
              </div>
            </div>
            
            <div className="action-button" onClick={handleShowCoursesForm}>
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                  <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                </svg>
              </div>
              <div className="text">
                <h3>Select Helpful Courses</h3>
                <p>Select courses that helped during this internship</p>
              </div>
            </div>
            
            {/* Company Evaluation Action Button */}
            <div className="action-button" onClick={handleShowCompanyEvaluation}>
              <div className="icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                </svg>
              </div>
              <div className="text">
                <h3>{selectedInternship.companyEvaluation ? 'View/Edit Evaluation' : 'Evaluate Company'}</h3>
                <p>{selectedInternship.companyEvaluation 
                  ? 'View or edit your company evaluation' 
                  : 'Provide feedback about this company'}</p>
              </div>
            </div>
              {selectedInternship.report && (selectedInternship.report.status === 'pending' || 
             selectedInternship.report.status === 'flagged' || 
             !selectedInternship.report.status) && (
              <div className="action-button" onClick={handleDeleteReport}>
                <div className="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"></polyline>
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  </svg>
                </div>
                <div className="text">
                  <h3>Delete Report</h3>
                  <p>Delete this internship report</p>
                </div>
              </div>
            )}
                        
            {selectedInternship.report && 
             (selectedInternship.report.status === 'flagged' || selectedInternship.report.status === 'rejected') && (
              <div className="action-button" onClick={handleShowAppealForm}>
                <div className="icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                <div className="text">
                  <h3>Appeal Decision</h3>
                  <p>Submit an appeal for your {selectedInternship.report.status === 'flagged' ? 'flagged' : 'rejected'} report</p>
                </div>
              </div>
            )}

            {/* Duplicate company evaluation button removed */}
          </div>
        </ActionSelectionModal>
      </ModalOverlay>
    );
  };

  return (
    <ReportManagementContainer>
      <PageHeader> 
        <PageTitle>Internship Report Management</PageTitle>
        </PageHeader>
      <ContentContainer>
        {renderReportsTab()}
      </ContentContainer>
      
      {showActionSelection && renderActionSelectionModal()}
      
      {showReportForm && selectedInternship && selectedInternship.company && (
        <ModalOverlay 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.target === e.currentTarget) {
              setShowReportForm(false);
            }
          }}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <InternshipReport 
              internship={selectedInternship} 
              onClose={() => setShowReportForm(false)} 
              onSave={(report) => {
                handleSaveReport(report);
                if (report && report.isFinalized) {
                  setShowReportForm(false);
                }
              }}
              existingReport={selectedInternship.report || null}
            />
          </ModalContent>
        </ModalOverlay>
      )}
      
{showCoursesForm && selectedInternship && (
  <MajorCoursesSelection 
    studentMajor={selectedInternship.major || "Computer Science and Engineering"}
    internship={selectedInternship} 
    onClose={() => setShowCoursesForm(false)} 
    onSave={(courses) => {
      handleSaveCourses(courses);
    }}
    selectedCourses={selectedInternship.helpfulCourses || []}
  />
)}
      
      {showAppealForm && selectedInternship && selectedInternship.report && (
        <ModalOverlay onClick={() => setShowAppealForm(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <AppealForm 
              report={selectedInternship.report}
              onClose={() => setShowAppealForm(false)} 
              onSubmit={(appealMessage) => {
                handleSubmitAppeal(appealMessage);
              }}
              existingAppeal={selectedInternship.report.appealMessage}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {showCompanyEvaluation && selectedInternship && (
        <ModalOverlay onClick={() => setShowCompanyEvaluation(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CompanyEvaluation 
              internship={selectedInternship}
              onClose={() => setShowCompanyEvaluation(false)}
              onSave={(evaluation) => {
                handleSaveCompanyEvaluation(evaluation);
              }}
              existingEvaluation={selectedInternship.companyEvaluation}
            />
          </ModalContent>
        </ModalOverlay>
      )}
    </ReportManagementContainer>
  );
};

export default ReportManagement;